import { useCallback, useState } from 'react';
import { format } from '@querodelivery/qd-date-helper';
import isEmpty from 'lodash/isEmpty';

import Tooltip from 'rc-tooltip';
import { Text } from '@querodelivery/uni-react';
import {
  ENUM_DELIVERY_TYPE,
  TRANSLATE_BILLING_TYPE,
  TRANSLATE_DELIVERY_TYPE,
} from '@/_features/deliveries/constants';
import { normalizeStatus } from '@/Utils/FnUtils';
import type { DeliveryAction } from '@/hooks/delivery/useDeliveryAction';
import useDeliveryAction from '@/hooks/delivery/useDeliveryAction';
import type { TaxPayerObj } from '@/Components/delivery/cancelDelivery/ModalCancelDelivery';
import ModalCancelDelivery, {
  TAX_PAYER_OPTIONS,
} from '@/Components/delivery/cancelDelivery/ModalCancelDelivery';
import ModalConnectDelivery from '@/Components/delivery/connectDelivery/ModalConnectDelivery';
import { useAppDispatch } from '@/_store/hooks';
import type { Delivery } from '@/_features/deliveries/types';
import WhatsappNumber from '@/Components/WhatsappNumber/WhatsappNumber';
import { MediaQueryMobile, STATUS_ENTREGA } from '@/Constants';
import styles from './styles.module.scss';
import { setSelectedDeliveryId } from '@/_store/modules/deliveries';
import { useCities } from '@/_hooks/use-cities';
import { DeliveryActionsDropdown } from '@/_components/delivery-actions-dropdown/delivery-actions-dropdown';
import { Table, useStackedTableLayout } from '@/_components/table';
import { DELIVERIES_TABLE_GRID_COLUMNS } from './constants';

interface DeliveryRowProps {
  delivery: Delivery;
  rechargeDeliveries(): void;
}

function DeliveryRow({ delivery, rechargeDeliveries }: DeliveryRowProps) {
  const dispatch = useAppDispatch();

  const isStackedTableLayout = useStackedTableLayout({
    customQuery: MediaQueryMobile,
  });

  const { getCityInfo } = useCities();

  const [actionSelected, setActionSelected] = useState<DeliveryAction | null>(
    null,
  );

  const cityInfo = getCityInfo(delivery.municipioId);

  const {
    actionLoading,
    availableActions,
    cancelDelivery,
    connectDeliveryToMotoboy,
    triggerDeliveryAction,
  } = useDeliveryAction(delivery, rechargeDeliveries);

  function handleCancelDelivery(
    action: DeliveryAction,
    taxPayers: TaxPayerObj[],
  ) {
    const collectTaxOfPlace = taxPayers.includes(TAX_PAYER_OPTIONS.PLACE);
    const passOnTaxForMotoboy = taxPayers.includes(TAX_PAYER_OPTIONS.MOTOBOY);

    cancelDelivery(action, collectTaxOfPlace, passOnTaxForMotoboy);
  }

  function handleActionDelivery(action: DeliveryAction) {
    switch (action.key) {
      case 'ASSOCIAR':
        setActionSelected(action);
        break;
      case 'CANCELAR':
        setActionSelected(action);
        break;
      default:
        triggerDeliveryAction(action);
    }
  }

  const renderActionSelected = useCallback(() => {
    if (!actionSelected) return null;

    switch (actionSelected.key) {
      case 'CANCELAR':
        return (
          <ModalCancelDelivery
            isFinishedDelivery={delivery.status === STATUS_ENTREGA.FINALIZADO}
            disabled={actionLoading == 'CANCELAR'}
            loading={actionLoading == 'CANCELAR'}
            opened
            onCancel={taxPayers =>
              handleCancelDelivery(actionSelected, taxPayers)
            }
            onClose={() => setActionSelected(null)}
          />
        );
      case 'ASSOCIAR':
        return (
          <ModalConnectDelivery
            disabled={actionLoading == 'ASSOCIAR'}
            loading={actionLoading == 'ASSOCIAR'}
            opened
            onConnect={(motoboyId: string) =>
              connectDeliveryToMotoboy(actionSelected, motoboyId)
            }
            delivery={delivery}
            onClose={() => setActionSelected(null)}
          />
        );
      default:
        return null;
    }
  }, [actionSelected]);

  const { deliveryType = ENUM_DELIVERY_TYPE.CLOUD } = delivery;

  const deliveryTypeLabel = delivery?.deliveryType
    ? TRANSLATE_DELIVERY_TYPE[delivery.deliveryType]
    : TRANSLATE_DELIVERY_TYPE.CLOUD;

  const billingTypeLabel = delivery?.billingType
    ? TRANSLATE_BILLING_TYPE[delivery.billingType]
    : delivery?.billingType;

  const formattedDeliveryTypeWithBilling =
    deliveryTypeLabel + (billingTypeLabel ? ` (${billingTypeLabel})` : '');

  const deliveryInactive = [
    STATUS_ENTREGA.CANCELADO,
    STATUS_ENTREGA.FINALIZADO,
    STATUS_ENTREGA.PENDENTE,
  ];

  const showWhatsapp =
    !deliveryInactive.includes(delivery.status) &&
    delivery?.nomeEntregador &&
    delivery?.deliverymanPhone;

  const acceptedDeliveryman = () => {
    if (showWhatsapp) {
      return (
        <div className={styles.containerWhatsApp}>
          <WhatsappNumber
            number={delivery.deliverymanPhone as string}
            textValue={delivery.nomeEntregador}
          />
        </div>
      );
    }

    if (delivery?.nomeEntregador) {
      return delivery?.nomeEntregador;
    }

    if (
      delivery.hasNeedForAttention &&
      delivery.status === STATUS_ENTREGA.PENDENTE &&
      deliveryType === ENUM_DELIVERY_TYPE.CLOUD
    ) {
      return (
        <div className={styles.attentionAlertContainer}>
          <Tooltip overlay="Esta entrega excedeu o raio de busca e ainda não encontrou entregadores disponíveis.">
            <td>Atenção</td>
          </Tooltip>
        </div>
      );
    }

    return '- - -';
  };

  if (isStackedTableLayout) {
    return (
      <Table.MobileCard
        onClick={() => {
          dispatch(setSelectedDeliveryId(delivery._id));
        }}
      >
        <Table.MobileItem label="Código">
          <Text fontWeight="semibold" tone="supporting">
            {delivery.codigo}
          </Text>
        </Table.MobileItem>
        <Table.MobileItem label="Loja">
          {delivery.nomeEstabelecimento}
        </Table.MobileItem>
        <Table.MobileItem label="Tipo">
          {formattedDeliveryTypeWithBilling}
        </Table.MobileItem>
        <Table.MobileItem label="Entregador">
          {acceptedDeliveryman()}
        </Table.MobileItem>
        <Table.MobileItem label="Status">
          {normalizeStatus(delivery.status)}
        </Table.MobileItem>
        <Table.MobileItem label="Data de criação">
          {delivery.createdAt
            ? format(new Date(delivery.createdAt), 'dd/MM/yyyy HH:mm')
            : null}
        </Table.MobileItem>
        <Table.MobileItem label="Franquia">
          {cityInfo?.nome} - {cityInfo?.uf}
        </Table.MobileItem>
        <Table.MobileItem label="Ações">
          {!isEmpty(availableActions) ? (
            <DeliveryActionsDropdown
              actions={availableActions}
              loading={!!actionLoading}
              onSelect={handleActionDelivery}
            />
          ) : null}
        </Table.MobileItem>
        {renderActionSelected()}
      </Table.MobileCard>
    );
  }

  return (
    <>
      <Table.Row
        key={delivery._id}
        gridTemplateColumns={DELIVERIES_TABLE_GRID_COLUMNS}
        onClick={() => {
          dispatch(setSelectedDeliveryId(delivery._id));
        }}
      >
        <Table.Cell>
          <Text
            fontWeight="semibold"
            tone="supporting"
            style={{
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
              minWidth: 0,
            }}
          >
            {delivery.codigo}
          </Text>
        </Table.Cell>
        <Table.Cell>
          <Text
            style={{
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
              minWidth: 0,
            }}
          >
            {delivery.nomeEstabelecimento}
          </Text>
        </Table.Cell>
        <Table.Cell>
          <Text>{formattedDeliveryTypeWithBilling}</Text>
        </Table.Cell>
        <Table.Cell>
          <Text>{acceptedDeliveryman()}</Text>
        </Table.Cell>
        <Table.Cell>
          <Text>{normalizeStatus(delivery.status)}</Text>
        </Table.Cell>
        <Table.Cell>
          <Text>
            {delivery.createdAt
              ? format(new Date(delivery.createdAt), 'dd/MM/yyyy HH:mm')
              : null}
          </Text>
        </Table.Cell>
        <Table.Cell>
          <Text>
            {cityInfo?.nome} - {cityInfo?.uf}
          </Text>
        </Table.Cell>
        <Table.Cell>
          {!isEmpty(availableActions) ? (
            <DeliveryActionsDropdown
              actions={availableActions}
              loading={!!actionLoading}
              onSelect={handleActionDelivery}
            />
          ) : null}
        </Table.Cell>
      </Table.Row>

      {renderActionSelected()}
    </>
  );
}

export default DeliveryRow;
